import { Fragment, useEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CommandItem } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { SearchResult } from "./useSearchEverywhereData";

export interface ResultRowProps {
  item: SearchResult;
  onSelect: (route: string) => void;
}

export function SearchResultRow({ item, onSelect }: ResultRowProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const Icon = item.icon;

  const openWithDelay = () => {
    timeoutRef.current = setTimeout(() => setTooltipOpen(true), 200);
  };

  const cancelAndClose = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTooltipOpen(false);
  };

  useEffect(() => {
    if (!tooltipOpen) return;
    window.addEventListener("scroll", cancelAndClose, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", cancelAndClose, { capture: true });
  }, [tooltipOpen]);

  return (
    <Tooltip open={tooltipOpen}>
      <TooltipTrigger asChild>
        <CommandItem
          value={item.id}
          disabled={!item.route}
          onSelect={() => item.route && onSelect(item.route)}
          onMouseEnter={openWithDelay}
          onMouseLeave={cancelAndClose}
          onBlur={cancelAndClose}
          className="flex w-full items-start gap-3 px-4 py-2 text-left text-sm rounded-none"
        >
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${item.iconClassName}`} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="wrap-break-word font-medium">{item.label}</span>
            {item.extraInfo && (
              <span className="wrap-break-word text-xs text-muted-foreground">
                {item.extraInfo}
              </span>
            )}
          </div>
          <span className="shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
            {item.typeLabel}
          </span>
        </CommandItem>
      </TooltipTrigger>
      <TooltipContent variant="card" side="right" sideOffset={8}>
        <p className="mb-2 font-semibold">{item.label}</p>
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {item.hoverDetails.map(
            ({ label, value }) =>
              value !== null && (
                <Fragment key={label}>
                  <span className="text-muted-foreground">{label}</span>
                  {Array.isArray(value) ? (
                    <span className="flex flex-col gap-1 items-start">
                      {value.map((v) => (
                        <Badge key={v} variant="secondary">
                          {v}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    <span className="font-medium">{value}</span>
                  )}
                </Fragment>
              ),
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
